import { useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { logDev } from "../lib/log";
import { STATUSES, PRIORITIES, STATUS_LABEL } from "../lib/constants";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "./ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { StatusBar, PriorityChip } from "./StatusBar";
import { DatePicker } from "./DatePicker";
import {
  Paperclip, ChatCircleDots, ClockClockwise, UploadSimple,
  FileText, Trash, DownloadSimple, Warning, Check, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function isOverdue(item) {
  if (!item?.eta) return false;
  if (item.status === "delivered") return false;
  const eta = new Date(item.eta);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eta < today;
}

export function OverdueBadge({ item, className = "" }) {
  if (!isOverdue(item)) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 chip bg-red-50 text-red-700 ${className}`}
      data-testid="overdue-badge"
    >
      <Warning size={11} weight="bold" /> Overdue
    </span>
  );
}

export default function ItemDetailSheet({ open, onClose, item: itemProp, onUpdated }) {
  const { user } = useAuth();
  const [baseline, setBaseline] = useState(itemProp);   // last-saved server state
  const [item, setItem] = useState(itemProp);           // local editable draft
  const [attachments, setAttachments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const fileInputRef = useRef(null);
  const sampleFormInputRef = useRef(null);

  const canEditAll = user?.role === "admin" || user?.role === "contractor" || user?.role === "project_manager";
  const canApprove = (user?.role === "admin" || user?.role === "project_manager")
    && baseline?.status === "awaiting_approval";
  const itemId = itemProp?.id;

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const [a, t] = await Promise.all([
        api.get(`/items/${itemId}/attachments`),
        api.get(`/items/${itemId}/timeline`),
      ]);
      setAttachments(a.data);
      setTimeline(t.data);
    } catch (e) {
      // Non-fatal — attachments/timeline are optional side panels — but we
      // log so a real API/network problem doesn't hide silently in dev.
      logDev("item-detail.attachments-timeline", e);
    }
  }, [itemId]);

  useEffect(() => {
    setBaseline(itemProp);
    setItem(itemProp);
    if (open && itemProp) load();
  }, [open, itemProp, load]);

  // Fields that participate in "unsaved changes" detection.
  const stripDeliveryKeys = (arr) =>
    Array.isArray(arr) ? arr.map(({ __key, ...rest }) => rest) : arr;

  const dirtyFields = (() => {
    if (!item || !baseline) return {};
    const diff = {};
    const trackable = [
      "name", "description", "status", "priority",
      "quantity", "unit", "eta", "location", "notes", "custom_fields",
      "code", "supplier", "scheme", "finish", "sample_image_url",
      "lead_time", "sample_approval_status", "purchase_order",
      "deliveries",
      "budget_qty", "budget_wastage", "budget_qty_incl_wastage",
      "budget_rate", "budget_amount",
      "current_qty", "current_wastage", "current_qty_incl_wastage",
      "current_rate", "current_amount", "budget_variance",
      "private_custom_fields",
    ];
    for (const f of trackable) {
      let a = item[f];
      let b = baseline[f];
      if (f === "deliveries") {
        a = stripDeliveryKeys(a);
        b = stripDeliveryKeys(b);
      }
      if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) {
        diff[f] = item[f];
      }
    }
    return diff;
  })();
  const isDirty = Object.keys(dirtyFields).length > 0;

  const saveChanges = async () => {
    if (!item || !isDirty) return;
    setBusy(true);
    try {
      const payload = { ...dirtyFields };
      if ("eta" in payload) payload.eta = payload.eta || null;
      if ("quantity" in payload) payload.quantity = Number(payload.quantity);
      if ("deliveries" in payload) payload.deliveries = stripDeliveryKeys(payload.deliveries);
      const { data } = await api.patch(`/items/${item.id}`, payload);
      setBaseline(data);
      setItem(data);
      toast.success("Changes saved");
      onUpdated?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const discardChanges = () => {
    if (!baseline) return;
    setItem(baseline);
  };

  // Warn users trying to close the sheet with unsaved edits.
  const requestClose = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Discard them and close?")) {
      return;
    }
    onClose();
  };

  const handleUpload = async (e, category = "general") => {
    const file = e.target.files?.[0];
    if (!file || !item) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    setBusy(true);
    try {
      await api.post(`/items/${item.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(category === "sample_approval" ? "Sample approval form uploaded" : "File uploaded");
      // Sample-approval upload can auto-advance the item's status server-side;
      // re-fetch the item so the sheet reflects it without needing a close/reopen.
      if (category === "sample_approval") {
        try {
          const { data } = await api.get(`/items/${item.id}`);
          setBaseline(data);
          setItem(data);
          if (typeof onUpdated === "function") onUpdated();
        } catch (e) {
          logDev("item.refetch-after-sample-upload", e);
          /* fall through — attachments/timeline still refresh */
        }
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      // Reset both inputs so the same file can be re-uploaded if needed.
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (sampleFormInputRef.current) sampleFormInputRef.current.value = "";
    }
  };

  const deleteAttachment = async (id) => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Attachment removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const attachFormSnapshot = async () => {
    if (!item?.id) return;
    setSavingForm(true);
    try {
      const { data } = await api.post(`/items/${item.id}/sample-form/attach`);
      toast.success(`Form attached: ${data.original_filename}`);
      load();
      if (typeof onUpdated === "function") onUpdated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to attach form");
    } finally {
      setSavingForm(false);
    }
  };

  const emailForm = async (recipients, message) => {
    if (!item?.id || recipients.length === 0) return { sent: [], failed: [] };
    const { data } = await api.post(`/items/${item.id}/sample-form/email`, {
      recipients,
      message: message || null,
    });
    return data;
  };

  const postComment = async () => {
    if (!comment.trim() || !item) return;
    setBusy(true);
    try {
      await api.post(`/items/${item.id}/comments`, { text: comment });
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/items/${item.id}/approve`);
      setBaseline(data);
      setItem(data);
      toast.success("Item approved");
      onUpdated?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  if (!item) {
    return (
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <div className="space-y-3 mt-8">
            <div className="h-6 bg-zinc-100 rounded animate-pulse" />
            <div className="h-32 bg-zinc-100 rounded animate-pulse" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const editStatus = canEditAll;
  const editEta = canEditAll;
  const editPriority = canEditAll;
  const editName = canEditAll;
  const editQty = canEditAll;
  const editNotes = canEditAll;
  const editDesc = canEditAll;
  const editLocation = canEditAll;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && requestClose()}>
      <SheetContent
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        data-testid="item-detail-sheet"
      >
        <SheetHeader className="px-6 pt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <PriorityChip priority={item.priority} />
            <OverdueBadge item={item} />
            <span className="label-eyebrow ml-auto">{STATUS_LABEL[item.status]}</span>
          </div>
          <SheetTitle className="font-display text-2xl font-bold tracking-tight pt-2">
            {item.name}
          </SheetTitle>
          <StatusBar status={item.status} compact />
          {canApprove && (
            <button
              onClick={handleApprove}
              disabled={busy}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors disabled:opacity-50"
              data-testid="detail-approve-button"
            >
              <Check size={16} weight="bold" /> Approve item
            </button>
          )}
        </SheetHeader>

        <Tabs defaultValue="details" className="px-6 pt-6 pb-10">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">
              Files ({attachments.length})
            </TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-5 mt-6">
            {canEditAll && (
              <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 -mb-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#007BFF]" />
                Edit any field, then click <span className="font-semibold text-[#001E44]">Save changes</span> at the bottom.
              </div>
            )}
            <Field label="Name" disabled={!editName}>
              <Input
                value={item.name}
                disabled={!editName}
                onChange={(e) => setItem((it) => ({ ...it, name: e.target.value }))}
                data-testid="detail-name-input"
              />
            </Field>
            <Field label="Description" disabled={!editDesc}>
              <Textarea
                rows={3}
                value={item.description || ""}
                disabled={!editDesc}
                onChange={(e) => setItem((it) => ({ ...it, description: e.target.value }))}
                data-testid="detail-desc-input"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <Select
                  value={item.status}
                  disabled={!editStatus}
                  onValueChange={(v) => setItem((it) => ({ ...it, status: v }))}
                >
                  <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        data-testid={`detail-status-option-${s.value}`}
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Priority" disabled={!editPriority}>
                <Select
                  value={item.priority}
                  disabled={!editPriority}
                  onValueChange={(v) => setItem((it) => ({ ...it, priority: v }))}
                >
                  <SelectTrigger data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Quantity" disabled={!editQty}>
                <Input
                  type="number" min="0" step="0.01"
                  value={item.quantity}
                  disabled={!editQty}
                  onChange={(e) => setItem((it) => ({ ...it, quantity: e.target.value }))}
                  data-testid="detail-qty-input"
                />
              </Field>
              <Field label="Unit" disabled={!editQty}>
                <Input
                  value={item.unit || ""}
                  disabled={!editQty}
                  onChange={(e) => setItem((it) => ({ ...it, unit: e.target.value }))}
                  placeholder="pcs, m, kg…"
                  data-testid="detail-unit-input"
                />
              </Field>
              <Field label="ETA" disabled={!editEta}>
                <DatePicker
                  value={item.eta ? item.eta.slice(0, 10) : ""}
                  disabled={!editEta}
                  onChange={(v) => setItem((it) => ({ ...it, eta: v }))}
                  data-testid="detail-eta-input"
                  placeholder="Select ETA"
                  compact
                />
              </Field>
            </div>
            <Field label="Location on site" disabled={!editLocation}>
              <Input
                value={item.location || ""}
                disabled={!editLocation}
                onChange={(e) => setItem((it) => ({ ...it, location: e.target.value }))}
                placeholder="e.g. Level 3 — East wing"
                data-testid="detail-location-input"
              />
            </Field>
            <Field label="Notes" disabled={!editNotes}>
              <Textarea
                rows={3}
                value={item.notes || ""}
                disabled={!editNotes}
                onChange={(e) => setItem((it) => ({ ...it, notes: e.target.value }))}
                data-testid="detail-notes-input"
              />
            </Field>

            <CustomFieldsEditor
              fields={item.custom_fields || {}}
              privateKeys={item.private_custom_fields || []}
              disabled={!canEditAll}
              onChange={(next) => setItem((it) => ({ ...it, custom_fields: next }))}
              onPrivateKeysChange={(next) =>
                setItem((it) => ({ ...it, private_custom_fields: next }))
              }
            />

            {/* --- Sourcing --- */}
            <SectionHeader eyebrow="Sourcing" title="Product & supplier" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Code / SKU" disabled={!canEditAll}>
                <Input
                  value={item.code || ""}
                  disabled={!canEditAll}
                  onChange={(e) => setItem((it) => ({ ...it, code: e.target.value }))}
                  placeholder="e.g. FT01"
                  data-testid="detail-code-input"
                />
              </Field>
              <Field label="Supplier" disabled={!canEditAll}>
                <Input
                  value={item.supplier || ""}
                  disabled={!canEditAll}
                  onChange={(e) => setItem((it) => ({ ...it, supplier: e.target.value }))}
                  data-testid="detail-supplier-input"
                />
              </Field>
              <Field label="Scheme" disabled={!canEditAll}>
                <Input
                  value={item.scheme || ""}
                  disabled={!canEditAll}
                  onChange={(e) => setItem((it) => ({ ...it, scheme: e.target.value }))}
                  placeholder="e.g. Neutral Scheme"
                  data-testid="detail-scheme-input"
                />
              </Field>
              <Field label="Finish" disabled={!canEditAll}>
                <Input
                  value={item.finish || ""}
                  disabled={!canEditAll}
                  onChange={(e) => setItem((it) => ({ ...it, finish: e.target.value }))}
                  placeholder="Matt / Gloss / Honed…"
                  data-testid="detail-finish-input"
                />
              </Field>
            </div>
            <Field label="Sample image URL" disabled={!canEditAll}>
              <Input
                value={item.sample_image_url || ""}
                disabled={!canEditAll}
                onChange={(e) => setItem((it) => ({ ...it, sample_image_url: e.target.value }))}
                placeholder="https://…"
                data-testid="detail-sample-image-input"
              />
              {item.sample_image_url && (
                <a
                  href={item.sample_image_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#007BFF] hover:underline mt-1 inline-block"
                >
                  Open sample →
                </a>
              )}
            </Field>

            {/* --- Order tracking --- */}
            <SectionHeader eyebrow="Order tracking" title="Approval & purchase" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sample approval">
                <Select
                  value={item.sample_approval_status || ""}
                  disabled={!canEditAll}
                  onValueChange={(v) =>
                    setItem((it) => ({ ...it, sample_approval_status: v === "__none__" ? "" : v }))
                  }
                >
                  <SelectTrigger data-testid="detail-sample-approval-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Lead time" disabled={!canEditAll}>
                <Input
                  value={item.lead_time || ""}
                  disabled={!canEditAll}
                  onChange={(e) => setItem((it) => ({ ...it, lead_time: e.target.value }))}
                  placeholder="e.g. 8-10 weeks"
                  data-testid="detail-lead-time-input"
                />
              </Field>
            </div>
            <Field label="Purchase order #" disabled={!canEditAll}>
              <Input
                value={item.purchase_order || ""}
                disabled={!canEditAll}
                onChange={(e) => setItem((it) => ({ ...it, purchase_order: e.target.value }))}
                placeholder="PO-1234"
                data-testid="detail-po-input"
              />
            </Field>

            <SampleApprovalUploads
              attachments={attachments.filter((a) => a.category === "sample_approval")}
              disabled={!canEditAll || busy}
              inputRef={sampleFormInputRef}
              onUpload={(e) => handleUpload(e, "sample_approval")}
              onDelete={deleteAttachment}
            />

            {/* --- Generated sample submission form --- */}
            {item.id && (
              <div className="rounded-xl border border-[#007BFF]/20 bg-[#007BFF]/5 p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-[#001E44]">Auto-generated sample submission form</div>
                    <div className="text-[11px] text-zinc-600 leading-relaxed">
                      Pre-filled with item spec, your company logo & LiveProcure branding — print or save as PDF.
                    </div>
                  </div>
                  <a
                    href={`${API}/items/${item.id}/sample-form`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold rounded-full px-4 py-2 bg-[#007BFF] hover:bg-[#0062cc] text-white transition-colors whitespace-nowrap"
                    data-testid="sample-form-link"
                  >
                    Open form
                  </a>
                </div>
                {canEditAll && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[#007BFF]/10">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={savingForm}
                      onClick={attachFormSnapshot}
                      data-testid="sample-form-attach"
                      className="h-8 text-xs rounded-full border-[#007BFF]/30 text-[#001E44] hover:bg-white"
                    >
                      {savingForm ? "Attaching…" : "Attach latest form to this item"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEmailFormOpen(true)}
                      data-testid="sample-form-email"
                      className="h-8 text-xs rounded-full border-[#007BFF]/30 text-[#001E44] hover:bg-white"
                    >
                      Email reviewers
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* --- Delivery schedule --- */}
            <SectionHeader eyebrow="Delivery schedule" title="Partial deliveries" />
            <DeliveriesEditor
              deliveries={item.deliveries || []}
              disabled={!canEditAll}
              onChange={(next) => setItem((it) => ({ ...it, deliveries: next }))}
            />

            {/* --- Financials (PRIVATE) --- */}
            <div className="flex items-center gap-2 pt-2">
              <span className="label-eyebrow text-red-700">Financials — private</span>
              <span className="chip text-[9px] bg-red-50 text-red-700 font-semibold" data-testid="private-badge">
                Hidden from public share
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 -mt-3">
              Never included in share links, PDF exports, or CSV exports.
            </p>
            <div className="rounded-xl border border-red-200 bg-red-50/30 p-4 space-y-4" data-testid="financials-section">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Budget qty">
                  <Input type="number" step="any"
                    value={item.budget_qty ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_qty: e.target.value === "" ? null : Number(e.target.value) }))}
                    data-testid="detail-budget-qty"
                  />
                </Field>
                <Field label="Budget wastage">
                  <Input type="number" step="any"
                    value={item.budget_wastage ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_wastage: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Budget qty incl. wastage">
                  <Input type="number" step="any"
                    value={item.budget_qty_incl_wastage ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_qty_incl_wastage: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Budget rate">
                  <Input type="number" step="any"
                    value={item.budget_rate ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_rate: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Budget amount">
                  <Input type="number" step="any"
                    value={item.budget_amount ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_amount: e.target.value === "" ? null : Number(e.target.value) }))}
                    data-testid="detail-budget-amount"
                  />
                </Field>
                <Field label="Budget variance">
                  <Input type="number" step="any"
                    value={item.budget_variance ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, budget_variance: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
              </div>
              <div className="border-t border-red-200 pt-4 grid grid-cols-2 gap-4">
                <Field label="Current qty">
                  <Input type="number" step="any"
                    value={item.current_qty ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, current_qty: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Current wastage">
                  <Input type="number" step="any"
                    value={item.current_wastage ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, current_wastage: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Current qty incl. wastage">
                  <Input type="number" step="any"
                    value={item.current_qty_incl_wastage ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, current_qty_incl_wastage: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Current rate">
                  <Input type="number" step="any"
                    value={item.current_rate ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, current_rate: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                </Field>
                <Field label="Current amount">
                  <Input type="number" step="any"
                    value={item.current_amount ?? ""}
                    disabled={!canEditAll}
                    onChange={(e) => setItem((it) => ({ ...it, current_amount: e.target.value === "" ? null : Number(e.target.value) }))}
                    data-testid="detail-current-amount"
                  />
                </Field>
              </div>
            </div>

            {!canEditAll && (
              <p className="text-xs text-muted-foreground italic">
                {user?.role === "project_manager"
                  ? "Project managers can view items but cannot modify them. Contractors are responsible for their procurement items."
                  : "You can view this item but only the assigned contractor can edit it."}
              </p>
            )}

            {canEditAll && (
              <div
                className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-zinc-200 flex items-center justify-between gap-3 shadow-[0_-8px_16px_-8px_rgba(0,0,0,0.08)]"
                data-testid="save-bar"
              >
                <div className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid="save-bar-status">
                  {isDirty ? (
                    <>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      <span className="font-semibold text-amber-700">Unsaved changes</span>
                    </>
                  ) : (
                    <>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span>All changes saved</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={discardChanges}
                    disabled={!isDirty || busy}
                    className="rounded-md h-9 text-xs uppercase tracking-wide font-semibold"
                    data-testid="detail-discard-button"
                  >
                    Discard
                  </Button>
                  <Button
                    type="button"
                    onClick={saveChanges}
                    disabled={!isDirty || busy}
                    className="bg-[#007BFF] hover:bg-[#0062cc] rounded-md h-9 text-xs uppercase tracking-wide font-semibold text-white disabled:opacity-40"
                    data-testid="detail-save-button"
                  >
                    {busy ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="attachments" className="space-y-4 mt-6">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || !canEditAll}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 rounded-md text-xs uppercase tracking-wide font-semibold w-full disabled:opacity-40"
                data-testid="upload-file-button"
              >
                <UploadSimple size={14} weight="bold" className="mr-2" />
                {busy ? "Uploading..." : "Upload file"}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {canEditAll ? "PDFs, images, docs — up to 20MB" : "Only the assigned contractor (or an admin) can upload."}
              </p>
            </div>
            {attachments.length === 0 ? (
              <div className="ctrl-card p-8 text-center text-sm text-muted-foreground">
                No attachments yet.
              </div>
            ) : (
              <ul className="space-y-2">
                {attachments.map((a) => (
                  <li key={a.id} className="ctrl-card p-3 flex items-center gap-3" data-testid={`attachment-${a.id}`}>
                    <div className="w-9 h-9 bg-zinc-100 rounded-sm flex items-center justify-center text-zinc-700">
                      <FileText size={18} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{a.original_filename}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">
                        {(a.size / 1024).toFixed(1)} KB · {a.uploaded_by_name}
                      </div>
                    </div>
                    <a
                      href={`${API}/attachments/${a.id}/download`}
                      target="_blank" rel="noreferrer"
                      className="p-2 text-zinc-500 hover:text-foreground transition-colors"
                      data-testid={`download-attachment-${a.id}`}
                      title="Download"
                    >
                      <DownloadSimple size={16} weight="bold" />
                    </a>
                    <button
                      onClick={() => deleteAttachment(a.id)}
                      className="p-2 text-zinc-400 hover:text-destructive transition-colors"
                      data-testid={`delete-attachment-${a.id}`}
                      title="Remove"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label className="label-eyebrow">Add a comment</Label>
              <Textarea
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Drop a note for the team…"
                data-testid="comment-input"
              />
              <Button
                onClick={postComment}
                disabled={busy || !comment.trim()}
                className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-md text-xs uppercase tracking-wide font-semibold"
                data-testid="post-comment-button"
              >
                <ChatCircleDots size={14} weight="bold" className="mr-2" />
                Post comment
              </Button>
            </div>
            <div className="relative space-y-3 pl-6 border-l-2 border-zinc-200">
              {timeline.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4">
                  Nothing here yet — actions and comments will appear in order.
                </div>
              ) : timeline.map((evt) => (
                <div key={evt.id} className="relative" data-testid={`timeline-${evt.id}`}>
                  <div className={`absolute -left-[29px] top-1 w-3 h-3 rounded-full ${
                    evt.type === "comment"
                      ? "bg-[hsl(var(--accent))]"
                      : "bg-zinc-400"
                  }`} />
                  <div className="ctrl-card p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{evt.user_name || "—"}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        {new Date(evt.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1.5 text-sm">
                      {evt.type === "system" ? (
                        <span className="text-muted-foreground italic flex items-center gap-1">
                          <ClockClockwise size={12} weight="bold" /> {evt.text}
                        </span>
                      ) : (
                        evt.text
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
      <ReviewerEmailDialog
        open={emailFormOpen}
        onOpenChange={setEmailFormOpen}
        onSend={emailForm}
      />
    </Sheet>
  );
}

function Field({ label, children, disabled }) {
  return (
    <div className="space-y-1.5">
      <Label className={`label-eyebrow ${disabled ? "opacity-50" : ""}`}>{label}</Label>
      {children}
    </div>
  );
}

function SectionHeader({ eyebrow, title }) {
  return (
    <div className="pt-2">
      <span className="label-eyebrow text-[#007BFF]">{eyebrow}</span>
      <h3 className="font-display text-sm font-bold text-[#001E44] mt-0.5">{title}</h3>
    </div>
  );
}

/**
 * Purpose-built upload slot for sample approval forms. Uses the existing
 * `/items/{id}/attachments` endpoint with `category=sample_approval` so it
 * lives alongside general files but renders in its own dedicated block
 * next to the Sample approval status selector.
 */
const REVIEWER_ROLES = [
  { key: "architect", label: "Architect" },
  { key: "engineer", label: "Engineer" },
  { key: "pm", label: "PM / Superintendent" },
  { key: "client", label: "Client" },
  { key: "builder", label: "Builder" },
];

function ReviewerEmailDialog({ open, onOpenChange, onSend }) {
  const [rows, setRows] = useState(() => REVIEWER_ROLES.map((r) => ({ ...r, email: "" })));
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(REVIEWER_ROLES.map((r) => ({ ...r, email: "" })));
      setMessage("");
    }
  }, [open]);

  // Cheap client-side email shape check — matches what the backend Pydantic
  // EmailStr accepts closely enough to catch obvious typos before we round-trip.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const filled = rows.filter((r) => r.email.trim().length > 0);
  const badRows = filled.filter((r) => !EMAIL_RE.test(r.email.trim()));
  const canSend = filled.length > 0 && badRows.length === 0 && !sending;

  const send = async () => {
    const recipients = filled.map((r) => ({ email: r.email.trim(), role: r.key }));
    if (recipients.length === 0) return;
    setSending(true);
    try {
      const res = await onSend(recipients, message);
      const failed = res?.failed || [];
      const sent = res?.sent || [];
      if (sent.length && !failed.length) {
        toast.success(`Emailed the form to ${sent.length} reviewer${sent.length > 1 ? "s" : ""}`);
        onOpenChange(false);
      } else if (sent.length && failed.length) {
        toast.warning(`Sent ${sent.length}, but ${failed.length} failed. Check the addresses.`);
      } else {
        toast.error(`Failed to send${failed[0]?.error ? ": " + failed[0].error : ""}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="reviewer-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight">Email sample form to reviewers</DialogTitle>
          <DialogDescription>
            The generated sample submission form will be attached to each email as an HTML file — reviewers open it in any browser to sign or Save as PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((r, i) => {
            const trimmed = r.email.trim();
            const invalid = trimmed.length > 0 && !EMAIL_RE.test(trimmed);
            return (
              <div key={r.key} className="grid grid-cols-[130px_1fr] items-center gap-2">
                <Label className="text-xs font-semibold text-zinc-600">{r.label}</Label>
                <div className="space-y-1">
                  <Input
                    type="email"
                    value={r.email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRows((prev) => prev.map((x, idx) => (idx === i ? { ...x, email: v } : x)));
                    }}
                    placeholder="name@example.com"
                    className={`h-9 text-sm ${invalid ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                    data-testid={`reviewer-email-${r.key}`}
                  />
                  {invalid && (
                    <div className="text-[10px] text-red-500 font-medium">Enter a valid email</div>
                  )}
                </div>
              </div>
            );
          })}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-zinc-600">Message (optional)</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Anything the reviewer should know before opening the form…"
              data-testid="reviewer-email-message"
              className="text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={send}
            disabled={!canSend}
            data-testid="reviewer-email-send"
            className="bg-[#007BFF] hover:bg-[#0062cc]"
          >
            {sending ? "Sending…" : "Send emails"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SampleApprovalUploads({ attachments, disabled, inputRef, onUpload, onDelete }) {
  const has = attachments.length > 0;
  return (
    <div className="space-y-2" data-testid="sample-approval-uploads">
      <div className="flex items-center justify-between">
        <Label className="label-eyebrow">Sample approval form</Label>
        {!disabled && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif"
              className="hidden"
              onChange={onUpload}
              data-testid="sample-approval-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#007BFF] hover:underline"
              data-testid="sample-approval-upload-button"
            >
              <UploadSimple size={12} weight="bold" />
              {has ? "Upload another" : "Upload signed form"}
            </button>
          </>
        )}
      </div>
      {has ? (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 p-2 rounded-lg border border-emerald-200 bg-emerald-50/40 text-xs"
              data-testid={`sample-approval-attachment-${a.id}`}
            >
              <Check size={13} weight="bold" className="text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#001E44] truncate">{a.original_filename}</div>
                <div className="text-[10px] text-zinc-500">
                  Uploaded by {a.uploaded_by_name || "someone"} · {new Date(a.created_at).toLocaleDateString()}
                </div>
              </div>
              <a
                href={a.storage_path}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] uppercase tracking-wider font-semibold text-[#007BFF] hover:underline shrink-0"
                data-testid={`sample-approval-view-${a.id}`}
              >
                View
              </a>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onDelete(a.id)}
                  className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                  data-testid={`sample-approval-delete-${a.id}`}
                >
                  <Trash size={12} weight="bold" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-zinc-400 italic">
          {disabled
            ? "No signed sample approval form uploaded yet."
            : "Upload the signed sample-approval form (PDF or image) — the client sees it on the shared schedule."}
        </p>
      )}
    </div>
  );
}

function DeliveriesEditor({ deliveries, disabled, onChange }) {
  // Keys must be stable across renders — otherwise React unmounts the
  // row on every keystroke and the input loses focus. We attach a
  // stable `__key` directly onto each delivery row. Because `update()`
  // uses `{ ...d, ...patch }`, the `__key` property is preserved across
  // edits. For rows arriving from the server without `__key`, we
  // back-fill once via effect.
  const newKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `d-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    if (!deliveries || deliveries.length === 0) return;
    if (deliveries.every((d) => d && d.__key)) return;
    onChange(deliveries.map((d) => (d.__key ? d : { ...d, __key: newKey() })));
    // onChange is intentionally omitted — parent passes an inline handler
    // that changes each render; the effect no-ops once all rows have keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries]);

  const add = () =>
    onChange([...(deliveries || []), { __key: newKey(), date: null, note: "" }]);
  const update = (idx, patch) =>
    onChange((deliveries || []).map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  const remove = (idx) =>
    onChange((deliveries || []).filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid="deliveries-editor">
      {(deliveries || []).length === 0 && (
        <p className="text-xs text-zinc-400 italic">
          {disabled ? "No delivery schedule." : "Add each partial delivery so everyone knows when to expect them."}
        </p>
      )}
      {(deliveries || []).map((d, idx) => (
        <div key={d.__key || `delivery-${idx}`} className="flex items-center gap-2" data-testid={`delivery-row-${idx}`}>
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 w-14 shrink-0">
            #{idx + 1}
          </span>
          <div className="w-40 shrink-0">
            <DatePicker
              value={(d.date || "").slice(0, 10)}
              disabled={disabled}
              onChange={(v) => update(idx, { date: v || null })}
              className="h-9 text-sm"
              data-testid={`delivery-date-${idx}`}
              placeholder="Date"
              compact
            />
          </div>
          <Input
            value={d.note || ""}
            disabled={disabled}
            onChange={(e) => update(idx, { note: e.target.value })}
            placeholder="Batch / area / phase"
            className="h-9 text-sm flex-1 min-w-0"
            data-testid={`delivery-note-${idx}`}
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => remove(idx)}
              className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
              data-testid={`delivery-remove-${idx}`}
            >
              <Trash size={13} weight="bold" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#007BFF] hover:underline"
          data-testid="add-delivery"
        >
          <Plus size={12} weight="bold" /> Add delivery
        </button>
      )}
    </div>
  );
}


/**
 * Per-item custom key/value fields. Edits update the parent's draft state
 * (via onChange). The parent's Save button commits the whole item, so
 * these rows never independently hit the API. Contractors and admins can
 * edit; PMs see read-only.
 */
function CustomFieldsEditor({ fields, privateKeys = [], disabled, onChange, onPrivateKeysChange }) {
  const entries = Object.entries(fields || {});
  const privateSet = new Set(privateKeys);

  const commit = (next) => {
    const cleaned = {};
    for (const [k, v] of Object.entries(next)) {
      const key = (k || "").trim();
      if (key) cleaned[key] = String(v ?? "");
    }
    onChange(cleaned);
  };

  const updateKey = (oldKey, newKey) => {
    const next = { ...fields };
    const val = next[oldKey];
    delete next[oldKey];
    next[newKey] = val;
    commit(next);
    // Migrate private-flag onto the new key name.
    if (privateSet.has(oldKey) && onPrivateKeysChange) {
      onPrivateKeysChange([...privateKeys.filter((k) => k !== oldKey), newKey]);
    }
  };

  const updateValue = (key, val) => {
    commit({ ...fields, [key]: val });
  };

  const remove = (key) => {
    const next = { ...fields };
    delete next[key];
    commit(next);
    if (privateSet.has(key) && onPrivateKeysChange) {
      onPrivateKeysChange(privateKeys.filter((k) => k !== key));
    }
  };

  const togglePrivate = (key) => {
    if (!onPrivateKeysChange) return;
    onPrivateKeysChange(
      privateSet.has(key)
        ? privateKeys.filter((k) => k !== key)
        : [...privateKeys, key]
    );
  };

  const addRow = () => {
    let i = 1;
    let name = "New field";
    while (Object.prototype.hasOwnProperty.call(fields || {}, name)) {
      i += 1;
      name = `New field ${i}`;
    }
    commit({ ...fields, [name]: "" });
  };

  return (
    <div className="space-y-2" data-testid="custom-fields-section">
      <div className="flex items-center justify-between">
        <Label className="label-eyebrow">Custom fields</Label>
        {!disabled && (
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#007BFF] hover:underline"
            data-testid="add-custom-field"
          >
            <Plus size={12} weight="bold" /> Add field
          </button>
        )}
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-zinc-400 italic">
          {disabled ? "No extra details for this item." : "Add a row for any extra detail you want to track on this item (e.g. PO number, supplier, finish)."}
        </p>
      ) : (
        <div className="space-y-2">
          {entries.map(([key, val], idx) => (
            <CustomFieldRow
              key={`cf-${idx}`}
              fieldKey={key}
              fieldValue={val}
              isPrivate={privateSet.has(key)}
              disabled={disabled}
              onChangeKey={(nk) => updateKey(key, nk)}
              onChangeValue={(nv) => updateValue(key, nv)}
              onTogglePrivate={() => togglePrivate(key)}
              onRemove={() => remove(key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CustomFieldRow({ fieldKey, fieldValue, isPrivate, disabled, onChangeKey, onChangeValue, onTogglePrivate, onRemove }) {
  const [k, setK] = useState(fieldKey);
  const [v, setV] = useState(fieldValue ?? "");
  const lastPropsRef = useRef({ fieldKey, fieldValue });
  useEffect(() => {
    if (lastPropsRef.current.fieldKey !== fieldKey) {
      lastPropsRef.current.fieldKey = fieldKey;
      setK(fieldKey);
    }
    if (lastPropsRef.current.fieldValue !== fieldValue) {
      lastPropsRef.current.fieldValue = fieldValue;
      setV(fieldValue ?? "");
    }
  }, [fieldKey, fieldValue]);

  return (
    <div
      className={`flex items-center gap-2 ${isPrivate ? "bg-red-50/40 rounded-lg -mx-2 px-2 py-1" : ""}`}
      data-testid={`custom-field-${fieldKey}`}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.querySelector("input:focus")?.blur();
        }
      }}
    >
      <Input
        value={k}
        disabled={disabled}
        onChange={(e) => setK(e.target.value)}
        onBlur={() => {
          const trimmed = k.trim();
          if (trimmed && trimmed !== fieldKey) onChangeKey(trimmed);
        }}
        placeholder="Field name"
        className="h-9 text-sm flex-1 min-w-0"
        data-testid={`custom-field-key-${fieldKey}`}
      />
      <Input
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== (fieldValue ?? "")) onChangeValue(v); }}
        placeholder="Value"
        className="h-9 text-sm flex-[1.4] min-w-0"
        data-testid={`custom-field-value-${fieldKey}`}
      />
      {!disabled && onTogglePrivate && (
        <button
          type="button"
          onClick={onTogglePrivate}
          className={`p-1.5 rounded-lg transition-colors shrink-0 text-[10px] font-semibold uppercase tracking-wider border ${
            isPrivate
              ? "bg-red-600 text-white border-red-700 hover:bg-red-700"
              : "text-zinc-500 border-zinc-200 hover:bg-zinc-100"
          }`}
          title={isPrivate ? "Private — hidden from public share" : "Mark private"}
          data-testid={`custom-field-private-toggle-${fieldKey}`}
        >
          {isPrivate ? "Private" : "Public"}
        </button>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0"
          aria-label={`Remove ${fieldKey}`}
          data-testid={`custom-field-remove-${fieldKey}`}
        >
          <Trash size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}

